import { Component, Input, Output, EventEmitter, forwardRef, ElementRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-custom-datepicker',
  template: `
    <div class="custom-datepicker-wrapper">
      <!-- Hidden HTML5 date input for native picker -->
      <input 
        #hiddenDateInput
        type="date" 
        class="hidden-date-input"
        [value]="html5Value"
        (change)="onHiddenDateChange($event)"
        [disabled]="disabled"
        tabindex="-1"
      />
      
      <!-- Visible text input for typing -->
      <input 
        #textInput
        type="text" 
        class="form-control visible-text-input"
        [value]="displayValue"
        (input)="onTextInput($event)"
        (blur)="onTextBlur($event)"
        (focus)="onTextFocus($event)"
        (keydown)="onKeyDown($event)"
        [disabled]="disabled"
        [placeholder]="placeholder || 'dd/MM/yyyy'"
        [style.width]="width"
        maxlength="10"
      />
      
      <!-- Calendar icon -->
      <i class="fa fa-calendar calendar-icon" 
         *ngIf="showIcon" 
         (click)="openCalendar()"
         [class.disabled]="disabled"></i>
    </div>
  `,
  styles: [`
    .custom-datepicker-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }
    
    .hidden-date-input {
      position: absolute;
      opacity: 0;
      pointer-events: none;
      z-index: -1;
      width: 0;
      height: 0;
    }
    
    .visible-text-input {
      border: 1px solid #ced4da;
      border-radius: 0.375rem;
      padding: 0.375rem 0.75rem;
      font-size: 0.875rem;
      line-height: 1.5;
      color: #495057;
      background-color: #fff;
      background-clip: padding-box;
      transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
      width: 100%;
      padding-right: 35px; /* Space for calendar icon */
    }
    
    .visible-text-input:focus {
      border-color: #80bdff;
      outline: 0;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
    
    .visible-text-input:disabled {
      background-color: #e9ecef;
      opacity: 1;
      cursor: not-allowed;
    }
    
    .calendar-icon {
      position: absolute;
      right: 10px;
      color: #6c757d;
      cursor: pointer;
      z-index: 1;
      font-size: 14px;
    }
    
    .calendar-icon:hover:not(.disabled) {
      color: #495057;
    }
    
    .calendar-icon.disabled {
      color: #adb5bd;
      cursor: not-allowed;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDatepickerComponent),
      multi: true
    }
  ]
})
export class CustomDatepickerComponent implements ControlValueAccessor {
  @ViewChild('hiddenDateInput', { static: false }) hiddenDateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('textInput', { static: false }) textInput!: ElementRef<HTMLInputElement>;

  @Input() placeholder: string = 'dd/MM/yyyy';
  @Input() disabled: boolean = false;
  @Input() width: string = '100%';
  @Input() showIcon: boolean = true;
  @Input() format: string = 'dd/MM/yyyy';
  @Output() valueChange = new EventEmitter<Date>();

  private _value: Date | null = null;
  private onChange = (value: Date | null) => { };
  private onTouched = () => { };

  public displayValue: string = '';
  private isTyping: boolean = false;

  @Input()
  get value(): Date | null {
    return this._value;
  }

  set value(val: Date | null) {
    if (val !== this._value) {
      this._value = val;
      this.updateDisplayValue();
      this.onChange(val);
      this.valueChange.emit(val);
    }
  }

  // HTML5 date input value (YYYY-MM-DD)
  get html5Value(): string {
    if (!this._value) return '';

    const year = this._value.getFullYear();
    const month = String(this._value.getMonth() + 1).padStart(2, '0');
    const day = String(this._value.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private updateDisplayValue(): void {
    if (!this._value) {
      this.displayValue = '';
      return;
    }

    const day = String(this._value.getDate()).padStart(2, '0');
    const month = String(this._value.getMonth() + 1).padStart(2, '0');
    const year = this._value.getFullYear();

    switch (this.format.toLowerCase()) {
      case 'mm/dd/yyyy':
        this.displayValue = `${month}/${day}/${year}`;
        break;
      case 'yyyy-mm-dd':
        this.displayValue = `${year}-${month}-${day}`;
        break;
      case 'dd-mm-yyyy':
        this.displayValue = `${day}-${month}-${year}`;
        break;
      default: // dd/MM/yyyy
        this.displayValue = `${day}/${month}/${year}`;
        break;
    }
  }

  // Handle typing in text input
  onTextInput(event: any): void {
    this.isTyping = true;
    const inputValue = event.target.value;

    // Auto-format while typing
    const formatted = this.formatAsUserTypes(inputValue);
    if (formatted !== inputValue) {
      event.target.value = formatted;
      this.displayValue = formatted;
    } else {
      this.displayValue = inputValue;
    }

    // Try to parse date if complete
    if (this.isCompleteDate(formatted)) {
      const parsedDate = this.parseDisplayValue(formatted);
      if (parsedDate && this.isValidDate(parsedDate)) {
        this._value = parsedDate;
        this.onChange(parsedDate);
        this.valueChange.emit(parsedDate);
      }
    }
  }

  // Format input as user types (add slashes automatically)
  private formatAsUserTypes(input: string): string {
    // Remove all non-numeric characters
    const numbers = input.replace(/\D/g, '');

    if (numbers.length === 0) return '';

    // Format based on current format
    if (this.format.toLowerCase() === 'dd/mm/yyyy' || this.format.toLowerCase() === 'dd/MM/yyyy') {
      if (numbers.length <= 2) return numbers;
      if (numbers.length <= 4) return numbers.substring(0, 2) + '/' + numbers.substring(2);
      return numbers.substring(0, 2) + '/' + numbers.substring(2, 4) + '/' + numbers.substring(4, 8);
    }

    return input; // Default: no formatting
  }

  // Check if input represents a complete date
  private isCompleteDate(input: string): boolean {
    const parts = input.split(/[\/\-]/);
    return parts.length === 3 && parts.every(part => part.length >= 1 && /^\d+$/.test(part));
  }

  // Parse display value to Date object
  private parseDisplayValue(displayValue: string): Date | null {
    if (!displayValue) return null;

    const parts = displayValue.split(/[\/\-]/);
    if (parts.length !== 3) return null;

    let day: number, month: number, year: number;

    switch (this.format.toLowerCase()) {
      case 'mm/dd/yyyy':
        month = parseInt(parts[0], 10);
        day = parseInt(parts[1], 10);
        year = parseInt(parts[2], 10);
        break;
      case 'yyyy-mm-dd':
        year = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10);
        day = parseInt(parts[2], 10);
        break;
      default: // dd/MM/yyyy or dd-mm-yyyy
        day = parseInt(parts[0], 10);
        month = parseInt(parts[1], 10);
        year = parseInt(parts[2], 10);
        break;
    }

    // Basic validation
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;
    if (year < 1900 || year > 2100) return null;

    return new Date(year, month - 1, day);
  }

  private isValidDate(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  onTextBlur(event: any): void {
    this.isTyping = false;

    const inputValue = event.target.value.trim();

    if (inputValue === '') {
      this.value = null;
      this.displayValue = '';
    } else {
      const parsedDate = this.parseDisplayValue(inputValue);
      if (parsedDate && this.isValidDate(parsedDate)) {
        this.value = parsedDate;
      } else {
        // Invalid date - reset to previous valid value or empty
        this.updateDisplayValue();
        if (this.textInput) {
          this.textInput.nativeElement.value = this.displayValue;
        }
      }
    }

    this.onTouched();
  }

  onTextFocus(event: any): void {
    // Select all text when focusing for easy replacement
    setTimeout(() => {
      if (this.textInput) {
        this.textInput.nativeElement.select();
      }
    }, 0);
  }

  // Handle special keys
  onKeyDown(event: KeyboardEvent): void {
    // Allow navigation keys
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Allow numbers and slashes/dashes
    if (/^[0-9\/\-]$/.test(event.key)) {
      return;
    }

    // Block other keys
    event.preventDefault();
  }

  // Open native calendar picker
  openCalendar(): void {
    if (!this.disabled && this.hiddenDateInput) {
      this.hiddenDateInput.nativeElement.focus();
      this.hiddenDateInput.nativeElement.click();
    }
  }

  // Handle date selection from native picker
  onHiddenDateChange(event: any): void {
    const dateValue = event.target.value;
    if (dateValue) {
      const selectedDate = new Date(dateValue);
      const adjustedDate = new Date(selectedDate.getTime() + selectedDate.getTimezoneOffset() * 60000);
      this.value = adjustedDate;

      // Focus back to text input
      if (this.textInput) {
        this.textInput.nativeElement.focus();
      }
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: Date | null): void {
    this._value = value;
    this.updateDisplayValue();

    if (this.textInput) {
      this.textInput.nativeElement.value = this.displayValue;
    }
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
